import { useEffect } from 'react';
import { showSyncBanner, type PlayerProfile } from '@goodtime/engine';
import floorUrl from '../assets/reel/forest_column_bg.png';

/**
 * U12 M2 / M4 (2026-05-31): says when something didn't reach the server.
 *
 * <p>Listens for {@code sync-error} events on the {@link PlayerProfile}
 * (from {@code useSlotUnlockSync} on a failed slot-unlock POST,
 * {@code useWalletSync} on a failed wallet GET, and the daily claims) and
 * shows a calm line under the top bar, so the player isn't left wondering
 * why a change doesn't survive the next session. The local profile stays
 * in charge of what they see now (a celebration is never rolled back over
 * a network hiccup), so the line says "it happened, and it'll catch up".
 *
 * <p>Drawn in Pixi on the overlay canvas over every screen (engine
 * `showSyncBanner`; producer 2026-10-14: everything in the game in Pixi).
 * It goes after a few seconds, or when tapped; several stack. Renders
 * nothing itself.
 */
export interface SyncErrorBannerProps {
  profile: PlayerProfile;
}

type Operation = 'slot-unlock' | 'wallet-sync' | 'daily-bonus' | 'daily-quest';

/** What the player reads for each. Tame on purpose: what they did already
 *  counts on this device; only the trip to the server didn't land. */
function syncErrorLine(operation: Operation | string): string {
  switch (operation) {
    case 'slot-unlock':
      return "Couldn't save your unlock. We'll keep trying";
    case 'wallet-sync':
      return "Couldn't sync your gems. They'll catch up next time";
    case 'daily-bonus':
      return "Couldn't claim your daily bonus. Tap the gift to try again";
    case 'daily-quest':
      return "Couldn't claim your daily quest. Tap it to try again";
    default:
      return "Couldn't sync with the server";
  }
}

const ART = { floor: floorUrl };

export function SyncErrorBanner({ profile }: SyncErrorBannerProps) {
  useEffect(() => {
    const sub = profile.events.on('sync-error', (event) => {
      void showSyncBanner(syncErrorLine(event.operation), ART);
    });
    return () => sub.dispose();
  }, [profile]);
  return null;
}
